/**
 * Painel informativo de previsão
 * Exibe informações sobre o período de previsão ativa
 * Busca dados automaticamente da sessão do vendedor
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, IconButton } from '@mui/material';
import { ShoppingCart, Analytics } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@hooks';

export const PainelPrevisao: React.FC = () => {
  const { vendedor } = useSession();
  const navigate = useNavigate();
  const [tempoRestante, setTempoRestante] = useState<string>('');
  const [tempoRestanteMs, setTempoRestanteMs] = useState<number>(0);

  // Extrai dados da sessão
  const indPrevisao = vendedor?.indPrevisao || 0;
  const indPrevisaoId = vendedor?.indPrevisaoId || 0;
  const indPrevisaoIni = vendedor?.indPrevisaoIni || '';
  const indPrevisaoFim = vendedor?.indPrevisaoFim || '';

  // Verifica se está nos últimos 5 minutos (300000 ms)
  const devesPulsar = tempoRestanteMs > 0 && tempoRestanteMs <= 300000;

  // Converte string de data brasileira (DD/MM/YYYY HH:MM:SS) para objeto Date
  const parseDataBrasileira = (dataStr: string): Date => {
    if (!dataStr) return new Date();

    // Formato esperado: DD/MM/YYYY HH:MM:SS ou DD/MM/YYYY HH:MM
    const partes = dataStr.split(' ');
    const [dia, mes, ano] = partes[0].split('/');
    const hora = partes[1] || '00:00:00';
    const [hh, mm, ss = '00'] = hora.split(':');

    // Cria data no formato correto (ano, mes-1, dia, hora, minuto, segundo)
    return new Date(
      parseInt(ano),
      parseInt(mes) - 1, // Mês começa em 0
      parseInt(dia),
      parseInt(hh),
      parseInt(mm),
      parseInt(ss)
    );
  };

  // Calcula o tempo restante
  useEffect(() => {
    if (!indPrevisaoFim) return;

    const calcularTempoRestante = () => {
      const agora = new Date();
      const dataFim = parseDataBrasileira(indPrevisaoFim);

      const diffMs = dataFim.getTime() - agora.getTime();

      // Armazena o tempo restante em milissegundos
      setTempoRestanteMs(diffMs);

      if (diffMs <= 0) {
        setTempoRestante('Finalizado');
        return;
      }

      const dias = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const horas = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutos = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const segundos = Math.floor((diffMs % (1000 * 60)) / 1000);

      let tempo = '';
      if (dias > 0) {
        tempo = `${dias}d ${horas}h ${minutos}m ${segundos}s`;
      } else if (horas > 0) {
        tempo = `${horas}h ${minutos}m ${segundos}s`;
      } else if (minutos > 0) {
        tempo = `${minutos}m ${segundos}s`;
      } else {
        tempo = `${segundos}s`;
      }

      setTempoRestante(tempo);
    };

    // Calcula imediatamente
    calcularTempoRestante();

    // Atualiza a cada segundo
    const interval = setInterval(calcularTempoRestante, 1000);

    return () => clearInterval(interval);
  }, [indPrevisaoFim]);

  // Formata data para exibição (já vem no formato DD/MM/YYYY HH:MM:SS)
  const formatarData = (data: string): string => {
    if (!data) return '';

    // Se a data já está no formato brasileiro, apenas retorna
    // Formato esperado: DD/MM/YYYY HH:MM:SS ou DD/MM/YYYY HH:MM
    const partes = data.split(' ');
    if (partes.length >= 2) {
      const dataFormatada = partes[0]; // DD/MM/YYYY
      const horaFormatada = partes[1].substring(0, 5); // HH:MM
      return `${dataFormatada} ${horaFormatada}`;
    }

    return data;
  };

  // Handler para navegar ao carrinho
  const handleCarrinhoClick = () => {
    navigate('/carrinho-previsao');
  };

  // Não exibe o painel se não houver previsão ativa
  if (indPrevisao <= 0) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        top: { xs: 50, sm: 20 },
        left: '50%',
        transform: 'translateX(-50%)',
        width: { xs: '100%', sm: '90%', md: '85%', lg: '1200px' },
        maxWidth: 'lg',
        zIndex: 1200,
        borderRadius: { xs: 0, sm: 2 },
        borderTop: { xs: 0, sm: `5px solid #026333` },
        backgroundColor: 'background.paper',
        transition: 'box-shadow 0.3s',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <Grid container alignItems="center" sx={{ p: { xs: 1.5, sm: 2 } }}>
        {/* Coluna 1: Ícone de Previsão */}
        <Grid
          item
          xs={2}
          sm={1.5}
          md={1.5}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Analytics
            sx={{
              fontSize: { xs: 32, sm: 40, md: 48 },
              color: 'success.main',
            }}
          />
        </Grid>

        {/* Coluna 2: Informações */}
        <Grid item xs={8} sm={9} md={9}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 0.3, sm: 0.5 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                Início:
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                {formatarData(indPrevisaoIni)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                Fim:
              </Typography>
              <Typography
                variant="body2"
                color="text.primary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                {formatarData(indPrevisaoFim)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                variant="body2"
                fontWeight={600}
                color="text.secondary"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
              >
                Tempo Restante:
              </Typography>
              <Typography
                variant="body1"
                fontWeight={700}
                color={tempoRestante === 'Finalizado' ? 'error' : (devesPulsar ? 'error' : 'primary')}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.9rem', md: '1rem' } }}
              >
                {tempoRestante}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Coluna 3: Ícone de Carrinho */}
        <Grid
          item
          xs={2}
          sm={1.5}
          md={1.5}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={handleCarrinhoClick}
            sx={{
              color: indPrevisaoId > 0 ? 'success.main' : 'action.disabled',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              // Adiciona animação de pulsação quando faltam 5 minutos ou menos
              ...(devesPulsar && {
                color: 'error.main',
                animation: 'pulse 1.5s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                  '50%': {
                    transform: 'scale(1.1)',
                    opacity: 0.8,
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                },
              }),
            }}
          >
            <ShoppingCart
              sx={{
                fontSize: { xs: 32, sm: 40, md: 48 },
              }}
            />
          </IconButton>
        </Grid>
      </Grid>
    </Paper>
  );
};
